/* Yard Mint cart behaviour for Horizon (cart page + cart drawer). Loaded from sections/main-cart.liquid
   and snippets/cart-drawer.liquid; safe to load twice.
   1. Designer bridge: Horizon re-renders the cart with section morphs and fires `shopify:cart:*` events,
      while the Yard Mint design-studio scripts (storefront-designs-pilot.js, product-designer-cart.js)
      listen for Dawn-style `cart:updated`. After every Horizon cart update/morph we dispatch `cart:updated`
      (and `cart:change`) on document so they re-check designs and re-decorate the new markup.
   2. Vaulted gate: while a [data-ym-vault-gate] is on the page (guest + vaulted item), any checkout control
      or checkout submit is sent to sign-in instead (port of live vaulted-cart-login-gate.liquid script).
   3. <ym-cart-fee-banner>: dismissible small-order fee banner, remembered for the session. */
(() => {
  'use strict';
  if (window.ymCart) return;
  window.ymCart = { version: 1 };

  /* ---------- 1. cart:updated bridge ---------- */
  let timer = 0;
  const notify = (delay = 180) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { source: 'ym-cart' } }));
      document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { source: 'ym-cart' } }));
    }, delay);
  };
  window.ymCart.notify = notify;

  const onStandardEvent = (event) => {
    const settle = event && event.promise && typeof event.promise.then === 'function' ? event.promise : null;
    if (settle) settle.then(() => notify(250), () => notify(250));
    else notify(250);
  };
  for (const name of ['shopify:cart:lines-update', 'shopify:cart:discount-update', 'cart-section:restored']) {
    document.addEventListener(name, onStandardEvent);
  }

  // Morph safety net: any structural change Horizon makes inside a cart component (not made by the
  // design scripts themselves, which mark their nodes) re-announces the cart.
  const OWNED = '[data-ym-owned],[data-epd-cart-owned]';
  const isOwned = (node) => node.nodeType === 1 && (node.matches(OWNED) || !!node.closest(OWNED));
  const observer = new MutationObserver((records) => {
    for (const r of records) {
      const target = r.target.nodeType === 1 ? r.target : r.target.parentElement;
      if (!target || !target.closest('cart-items-component')) continue;
      if (target.closest(OWNED)) continue;
      const nodes = [...r.addedNodes, ...r.removedNodes].filter((n) => n.nodeType === 1);
      if (!nodes.length || nodes.every((n) => isOwned(n) || n.matches('style,script'))) continue;
      notify(220);
      return;
    }
  });
  const observe = () => observer.observe(document.body, { childList: true, subtree: true });
  if (document.body) observe();
  else document.addEventListener('DOMContentLoaded', observe, { once: true });

  /* ---------- 2. vaulted gate ---------- */
  const gate = () => document.querySelector('[data-ym-vault-gate]');
  const CHECKOUT =
    '#checkout,button[name="checkout"],input[name="checkout"],a[href="/checkout"],a[href^="/checkout?"],.additional-checkout-buttons,.cart__dynamic-checkout-buttons,shopify-accelerated-checkout-cart';
  const toLogin = (event, g) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    window.location.assign(g.dataset.loginUrl || '/account/login');
  };
  document.addEventListener(
    'click',
    (event) => {
      const g = gate();
      if (!g || !(event.target instanceof Element) || !event.target.closest(CHECKOUT)) return;
      toLogin(event, g);
    },
    true
  );
  document.addEventListener(
    'submit',
    (event) => {
      const g = gate();
      if (!g || !event.submitter || event.submitter.name !== 'checkout') return;
      toLogin(event, g);
    },
    true
  );

  /* ---------- 3. fee banner ---------- */
  const KEY = 'ym-cart-fee-banner-closed';
  const dismissed = () => {
    try {
      return sessionStorage.getItem(KEY) === 'true';
    } catch {
      return false;
    }
  };
  if (dismissed()) document.documentElement.classList.add('ym-fee-dismissed');

  class YmCartFeeBanner extends HTMLElement {
    connectedCallback() {
      if (dismissed()) document.documentElement.classList.add('ym-fee-dismissed');
      if (this.ymBound) return;
      this.ymBound = true;
      this.addEventListener('click', (event) => {
        if (!(event.target instanceof Element) || !event.target.closest('[data-ym-fee-close]')) return;
        try {
          sessionStorage.setItem(KEY, 'true');
        } catch {}
        document.documentElement.classList.add('ym-fee-dismissed');
      });
    }
  }
  if (!customElements.get('ym-cart-fee-banner')) customElements.define('ym-cart-fee-banner', YmCartFeeBanner);
})();
